import { useEffect } from 'react';
import { clsx } from 'clsx';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { PageBanner, type PageBannerVariant } from '@interxfi/design-system/components/page-banner/page-banner';
import { Typography } from '@interxfi/design-system/components/typography';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import { useRealmIndicatorContext } from '../realm-indicator-context';
import { getRealmTypeForRealmName, type RealmType } from '../realm-utils';
import styles from './realm-page-banner.module.scss';

const variantByRealmType: Record<RealmType, PageBannerVariant> = {
  sandbox: 'secondary',
  live: 'green',
  integrated: 'primary',
  unknown: 'neutral',
};

const realmNameByRealmType: Record<Exclude<RealmType, 'unknown'>, string> = {
  sandbox: 'Sandbox',
  live: 'Live',
  integrated: 'Integrated',
};

function getContentForRealmType(realmType: RealmType, showRealmNameOnly: boolean) {
  if (realmType === 'unknown') {
    return '';
  }

  const realmName = realmNameByRealmType[realmType];

  if (showRealmNameOnly) {
    return realmName;
  }

  switch (realmType) {
    case 'sandbox':
      return (
        <span>
          You&apos;re using Interchange with test data in <Typography weight="bold">{realmName}</Typography>.
        </span>
      );
    case 'live':
      return (
        <span>
          You&apos;re using Interchange with real data in <Typography weight="bold">{realmName}</Typography>.
        </span>
      );
    case 'integrated':
      return (
        <span>
          You&apos;re using Interchange with test data and real integrations in <Typography weight="bold">{realmName}</Typography>.
        </span>
      );
  }
}

export function RealmPageBanner() {
  const { activeRealmAccess } = useActiveRealmAPIContext();
  const { setShowBanner, showBanner } = useRealmIndicatorContext();
  const isMobile = useBreakpoint('sm');
  const realmType = getRealmTypeForRealmName(activeRealmAccess?.realm?.data?.spec?.name);

  useEffect(() => {
    setShowBanner(realmType !== 'unknown');
  }, [realmType, setShowBanner]);

  const isVisible = showBanner && realmType !== 'unknown';

  return (
    <PageBanner
      className={clsx(styles.realmPageBanner, isVisible && styles.isVisible)}
      onDismiss={
        isVisible
          ? () => {
              setShowBanner(false);
            }
          : undefined
      }
      variant={variantByRealmType[realmType]}
    >
      <span className={styles.content}>{getContentForRealmType(realmType, isMobile)}</span>
    </PageBanner>
  );
}
